import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, Switch, Alert, Button, Typography, message } from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import { upsertEmailRecipientRule } from '../api';
import { emailTypeLabel } from '../emailRecipientTypes';

const { Text } = Typography;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validator for antd "tags" mode Select — every entered token must be an email.
const emailListValidator = (_rule, value) => {
    if (!value || value.length === 0) return Promise.resolve();
    const bad = value.filter((v) => !EMAIL_RE.test((v || '').trim()));
    if (bad.length) return Promise.reject(new Error(`Invalid email${bad.length > 1 ? 's' : ''}: ${bad.join(', ')}`));
    return Promise.resolve();
};

const tagSelectProps = {
    mode: 'tags',
    tokenSeparators: [',', ' '],
    open: false,            // free text only — no dropdown suggestions
    style: { width: '100%' },
};

// A CC/BCC field with a "Restore defaults" link shown when any of the
// pre-filled default recipients has been removed.
const RecipientField = ({ form, name, label, placeholder, defaults }) => {
    const current = Form.useWatch(name, form) || [];
    const missing = (defaults || []).filter((d) => !current.includes(d));

    const restore = () => form.setFieldValue(name, [...current, ...missing]);

    return (
        <Form.Item
            name={name}
            label={(
                <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <span>{label}</span>
                    {missing.length > 0 && (
                        <Button type="link" size="small" icon={<UndoOutlined />} onClick={restore} style={{ padding: 0 }}>
                            Restore default{missing.length > 1 ? 's' : ''}
                        </Button>
                    )}
                </span>
            )}
            rules={[{ validator: emailListValidator }]}
        >
            <Select {...tagSelectProps} placeholder={placeholder} />
        </Form.Item>
    );
};

const EmailRecipientRuleModal = ({ open, rule, companyId, onClose, onSuccess }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [overrideTo, setOverrideTo] = useState(false);

    useEffect(() => {
        if (open && rule) {
            const hasOverride = Array.isArray(rule.to_override) && rule.to_override.length > 0;
            setOverrideTo(hasOverride);
            form.setFieldsValue({
                to_override: rule.to_override || [],
                cc: rule.cc || [],
                bcc: rule.bcc || [],
            });
        }
    }, [open, rule, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            await upsertEmailRecipientRule(companyId, rule.email_type, {
                to_override: overrideTo ? (values.to_override || []) : null,
                cc: values.cc || [],
                bcc: values.bcc || [],
            });
            message.success('Recipient rule saved');
            onSuccess();
        } catch (err) {
            if (err.errorFields) return;
            message.error(err.message || 'Failed to save recipient rule');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={rule ? `Recipients — ${emailTypeLabel(rule.email_type)}` : 'Edit recipients'}
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={submitting}
            destroyOnClose
            width={560}
        >
            <Form form={form} layout="vertical">
                <Form.Item label="Override the To recipients">
                    <Switch checked={overrideTo} onChange={setOverrideTo} />
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                        Replace the system&apos;s default recipients entirely
                    </Text>
                </Form.Item>

                {overrideTo && (
                    <Form.Item
                        name="to_override"
                        label="To"
                        rules={[
                            { required: true, message: 'Add at least one recipient, or turn off the override' },
                            { validator: emailListValidator },
                        ]}
                        extra="The email will be sent only to these addresses instead of the usual recipients."
                    >
                        <Select {...tagSelectProps} placeholder="Add recipient email(s)" />
                    </Form.Item>
                )}

                <RecipientField
                    form={form}
                    name="cc"
                    label="CC"
                    placeholder="Add CC email(s)"
                    defaults={rule?.default_cc}
                />

                <RecipientField
                    form={form}
                    name="bcc"
                    label="BCC"
                    placeholder="Add BCC email(s)"
                    defaults={rule?.default_bcc}
                />

                <Alert
                    type="info"
                    showIcon
                    message="Default GoHire recipients are pre-filled — remove any pill to stop sending to it. Overrides apply only to this company, for this email type."
                />
            </Form>
        </Modal>
    );
};

export default EmailRecipientRuleModal;
